import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { App, Button, Modal, Progress, Spin, Tooltip } from 'antd';
import { FileDownloadStatusEnum } from '../../../enums/FileDownloadStatusEnum';
import { useFileDownload } from '../../../hooks/useFileDownload';
import type FailedFile from '../../../interfaces/FailedFile';
import type ProcessStatus from '../../../interfaces/ProcessStatus';
import { FilesDownloadErrorForm } from '../../Forms/FilesDownloadErrorForm/FilesDownloadErrorForm';
import { Icon } from '../../Core/Icon';
import TruncatedTooltip from '../TruncatedTooltip';

const DownloadProgressDrawer = () => {
  const { t } = useTranslation();
  const { notification, modal } = App.useApp();

  /**
   * Displays a modal dialog listing files that were excluded from the download.
   * @param failedFilesList - List of files that failed to download or were excluded
   */
  const openExcludedFilesModal = (failedFilesList: FailedFile[]): void => {
    modal.warning({
      title: t('common.excludedFilesTitle'),
      footer: null,
      content: (
        <FilesDownloadErrorForm
          failedFilesList={failedFilesList}
          onClose={() => Modal.destroyAll()}
        />
      ),
    });
  };

  // Controls whether the drawer UI is minimized (collapsed)
  const [isMinimized, setIsMinimized] = useState(false);

  // Holds the previous process status list for comparison in effects
  const prevProcessStatusesRef = useRef<ProcessStatus[]>([]);

  // Main download state and actions (from context/hook)
  const { fileDownloadProcessStatuses, handleCancelAllFileDownloads } = useFileDownload();

  /**
   * Memoized title based on the state of any ongoing download process.
   * Shows 'downloadPreparation' if any is in progress, otherwise 'downloadReady'.
   */
  const title: string = useMemo(() => {
    const inProgressStatuses = fileDownloadProcessStatuses.filter(
      (process) => process.status === FileDownloadStatusEnum.Processing,
    );
    if (inProgressStatuses.length === 0) {
      return t('common.downloadReady');
    }
    const isOnlyDirectFileDownloads = inProgressStatuses.every(
      (process) => process.isDirectFileDownload,
    );
    if (isOnlyDirectFileDownloads) {
      return t('common.preparingFileForDownload');
    }
    return t('common.downloadPreparation');
  }, [fileDownloadProcessStatuses, t]);

  /**
   * When a new download is queued (list length changes), expand the drawer if minimized.
   */
  useEffect(() => {
    setIsMinimized(false);
  }, [fileDownloadProcessStatuses.length]);

  /**
   * Compare previous and current process status lists.
   * - If new processes started, automatically expand the drawer.
   * - Show notifications if a process just finished with partial success and excluded files.
   * - Always sync prevProcessStatusesRef to the latest list at the end.
   */
  useEffect(() => {
    const prevProcessStatuses = prevProcessStatusesRef.current;

    // Find processes previously considered 'Processing'
    const prevInProgressIds = prevProcessStatuses
      .filter((p) => p.status === FileDownloadStatusEnum.Processing)
      .map((p) => p.processId);

    // Find currently active processing processes
    const currentInProgress = fileDownloadProcessStatuses.filter(
      (p) => p.status === FileDownloadStatusEnum.Processing,
    );

    // If there is a new in-progress process not found previously, un-minimize the drawer
    const hasNewInProgress = currentInProgress.some(
      (p) => !prevInProgressIds.includes(p.processId),
    );

    if (hasNewInProgress) {
      setIsMinimized(false);
    }

    // If a process newly finished as PartialSuccess and has failed files, notify
    const prevPartialIds = prevProcessStatuses
      .filter((p) => p.status === FileDownloadStatusEnum.PartialSuccess)
      .map((p) => p.processId);

    fileDownloadProcessStatuses
      .filter(
        (p) =>
          p.status === FileDownloadStatusEnum.PartialSuccess &&
          !!p.failedFilesList?.length &&
          !prevPartialIds.includes(p.processId),
      )
      .forEach(() => {
        notification.warning({
          message: t('common.downloadCompletedWithExclusions'),
          placement: 'top',
        });
      });

    // Sync the previous status ref for the next update/effect cycle
    prevProcessStatusesRef.current = fileDownloadProcessStatuses;
  }, [fileDownloadProcessStatuses, notification, t]);

  // Hide entire drawer if no active download processes exist
  if (fileDownloadProcessStatuses.length === 0) {
    return null;
  }

  const toggleMinimized = (): void => {
    setIsMinimized((previous) => !previous);
  };

  return (
    <div
      id="download-progress-drawer"
      role="region"
      aria-label={t('a11y.downloadProgress')}
      className={clsx(
        'border-gray-medium z-50 w-full rounded-md border bg-white transition-all duration-200 md:w-[400px] lg:w-[500px]',
        isMinimized ? 'h-12' : 'h-80',
      )}
    >
      {/* Drawer Header: shows title and minimize/cancel controls */}
      <div
        className={clsx(
          'bg-gray-light flex items-center justify-between rounded-t-md border-b px-3 py-2',
          isMinimized ? 'border-transparent' : 'border-gray-medium',
        )}
      >
        <div className="text-blue-dark text-base font-medium">{title}</div>
        <div className="flex items-center">
          {/* Minimize/collapse toggle button */}
          <Button
            type="text"
            size="small"
            className="flex items-center pl-0"
            aria-label={
              isMinimized ? t('a11y.expandDownloadProgress') : t('a11y.minimizeDownloadProgress')
            }
            onClick={(clickEvent) => {
              clickEvent.stopPropagation();
              toggleMinimized();
            }}
            icon={
              <Icon
                name="ChevronDownIcon"
                className={clsx(
                  'mt-1 h-5 w-5 transform transition-transform duration-200',
                  isMinimized ? 'rotate-180' : '',
                )}
              />
            }
          />
          {/* Cancel all downloads button */}
          <Button
            type="text"
            size="small"
            onClick={(clickEvent) => {
              clickEvent.stopPropagation();
              void handleCancelAllFileDownloads();
            }}
            className="flex items-center pl-0"
            aria-label={t('a11y.cancelAllDownloads')}
            icon={<Icon name="XIcon" className="mt-1 h-4 w-4" />}
          />
        </div>
      </div>
      {/* Drawer body: only visible when not minimized */}
      {!isMinimized && (
        <div className="divide-gray flex h-full max-h-fit flex-col divide-y overflow-y-auto px-3 pb-14">
          {fileDownloadProcessStatuses.map((process) => (
            <div
              key={process.processId}
              className="relative flex min-h-[35px] items-center justify-between transition-all duration-200"
            >
              {/* Process status and name/info */}
              <div className="flex items-center">
                <div className="max-w-60 truncate">
                  <div className="text-blue-dark text-sm font-medium">
                    <TruncatedTooltip>
                      {((): string => {
                        if (process.isDirectFileDownload && process.name) {
                          return process.name;
                        }
                        // Status description texts per process status
                        switch (process.status) {
                          case FileDownloadStatusEnum.Pending:
                            return t('common.preparingFileForDownload');
                          case FileDownloadStatusEnum.Processing:
                            return t('common.compressionOfFilesInProgress');
                          case FileDownloadStatusEnum.Verifying:
                            return (
                              t('common.verifyingFileIntegrity') || 'Verifying file integrity...'
                            );
                          case FileDownloadStatusEnum.Success:
                          case FileDownloadStatusEnum.PartialSuccess:
                            return process.name || t('common.compressionCompleted');
                          case FileDownloadStatusEnum.Failed:
                            return t('common.compressionFailed');
                          case FileDownloadStatusEnum.IntegrityCheckFailed:
                            return (
                              t('errors.fileIntegrityCheckFailed') || 'File integrity check failed'
                            );
                          default:
                            return 'N/D';
                        }
                      })()}
                    </TruncatedTooltip>
                  </div>
                </div>
              </div>
              {/* Process status action/icon area */}
              <div className="flex items-center">
                {(() => {
                  // Render action icons/buttons based on process status
                  switch (process.status) {
                    case FileDownloadStatusEnum.Pending:
                      // In pending: show loading spinner
                      return <Spin size="small" />;
                    case FileDownloadStatusEnum.Processing:
                      if (process.isDirectFileDownload) {
                        return (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-dark text-xs">
                              {t('common.preparingForDownload')}
                            </span>
                            <span className="text-sm">{process.progressPercentage}%</span>
                          </div>
                        );
                      }
                      // Show progress percent during archive/compression processing
                      return (
                        <div>
                          <span className="text-sm">{process.progressPercentage}%</span>
                        </div>
                      );
                    case FileDownloadStatusEnum.Verifying:
                      // Show a spinner for 'verifying'
                      return <Spin size="small" />;
                    case FileDownloadStatusEnum.Success:
                      // Show download and success icons when fully successful
                      return (
                        <div className="flex items-center gap-1">
                          <Tooltip placement="left" title={t('common.download')}>
                            <Button
                              type="text"
                              size="small"
                              className="px-2! py-0.5!"
                              aria-label={t('common.download')}
                              onClick={() => {
                                // Start browser download for this file
                                const downloadUrl = process.additionalData;
                                if (downloadUrl) {
                                  const link = document.createElement('a');
                                  link.href = downloadUrl;
                                  link.setAttribute('download', process.name || 'files.zip');
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              }}
                            >
                              <Icon name="CloudDownloadIcon" className="mt-0.5 h-5 w-5" />
                            </Button>
                          </Tooltip>
                          <Icon name="CircleCheckIcon" className="text-green-dark h-5 w-5" />
                          {process.integrityVerified && (
                            <Tooltip
                              placement="left"
                              title={t('common.fileIntegrityVerified') || 'File integrity verified'}
                            >
                              <div>
                                <Icon name="ShieldCheckIcon" className="text-green-dark h-5 w-5" />
                              </div>
                            </Tooltip>
                          )}
                        </div>
                      );
                    case FileDownloadStatusEnum.PartialSuccess:
                      // Show download and warning/exclusion info for partially successful downloads
                      return (
                        <div className="flex items-center gap-1">
                          <Tooltip placement="left" title={t('common.download')}>
                            <Button
                              type="text"
                              size="small"
                              className="px-2! py-0.5!"
                              aria-label={t('common.download')}
                              onClick={() => {
                                // Download the partial archive
                                const downloadUrl = process.additionalData;
                                if (downloadUrl) {
                                  const link = document.createElement('a');
                                  link.href = downloadUrl;
                                  link.setAttribute('download', process.name || 'files.zip');
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }
                              }}
                            >
                              <Icon name="CloudDownloadIcon" className="mt-0.5 h-5 w-5" />
                            </Button>
                          </Tooltip>
                          {/* Excluded files button */}
                          <Tooltip placement="top" title={t('common.viewExcludedFiles')}>
                            <button
                              type="button"
                              aria-label={t('common.viewExcludedFiles')}
                              onClick={() => openExcludedFilesModal(process.failedFilesList ?? [])}
                              className="text-yellow-dark border-yellow-dark hover:bg-yellow-light flex cursor-pointer items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold transition-colors"
                            >
                              <Icon name="TriangleAlertIcon" className="h-3.5 w-3.5" />
                              <span>{process.failedFilesList?.length}</span>
                              <Icon name="ChevronRightIcon" className="h-3 w-3" />
                            </button>
                          </Tooltip>
                        </div>
                      );
                    case FileDownloadStatusEnum.Failed:
                      return (
                        <div className="flex items-center gap-0.5">
                          <Icon name="CircleAlertIcon" className="text-red-dark h-5 w-5" />
                          <span className="text-red-dark text-sm">
                            {process.isDirectFileDownload
                              ? t('common.failed')
                              : t('common.compressionFailed')}
                          </span>
                        </div>
                      );
                    case FileDownloadStatusEnum.IntegrityCheckFailed:
                      // Failed due to integrity: show relevant error
                      return (
                        <div className="flex items-center gap-0.5">
                          {/* Error icon */}
                          <Icon name="CircleAlertIcon" className="text-red-dark h-5 w-5" />
                          <span className="text-red-dark text-sm">
                            {t('errors.fileIntegrityCheckFailed') || 'Integrity check failed'}
                          </span>
                        </div>
                      );
                    default:
                      // Fallback for unknown status
                      return 'N/D';
                  }
                })()}
              </div>
              {/* Progress bar for Processing, Pending, Failed, Verifying or IntegrityCheckFailed statuses */}
              {[
                FileDownloadStatusEnum.Processing,
                FileDownloadStatusEnum.Pending,
                FileDownloadStatusEnum.Failed,
                FileDownloadStatusEnum.Verifying,
                FileDownloadStatusEnum.IntegrityCheckFailed,
              ].includes(process.status) && (
                <Progress
                  status={
                    process.status === FileDownloadStatusEnum.Failed ||
                    process.status === FileDownloadStatusEnum.IntegrityCheckFailed
                      ? 'exception'
                      : 'active'
                  }
                  className="absolute! right-0! bottom-0! left-0! z-10!"
                  size={{ height: 1 }}
                  percent={process.progressPercentage ?? 0}
                  showInfo={false}
                  style={{ height: '1px', minHeight: '1px' }}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DownloadProgressDrawer;
